import React, { useState } from "react";
import {
  CheckCircle2,
  Database,
  LoaderCircle,
  RadioTower,
  Save,
  Settings,
  SlidersHorizontal,
} from "lucide-react";

import { useConfig } from "../../context/configContext";
import api from "../interceptors.js";
import "./index.css";

const Configuration = () => {
  const { config, updateConfig } = useConfig();

  const [form, setForm] = useState({
    dispensers: config.dispensers,
    guns: config.guns,
    mqttBroker: "",
    dbHost: "",
    dbUser: "",
    dbPassword: "",
    logInterval: 10,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    const payload = {
      system: {
        dispensers: Number(form.dispensers),
        guns: Number(form.guns),
      },
      mqtt: { mqttBroker: form.mqttBroker },
      database: {
        host: form.dbHost,
        user: form.dbUser,
        password: form.dbPassword,
      },
      logging: { logInterval: Number(form.logInterval) },
    };

    updateConfig({
      dispensers: Number(form.dispensers),
      guns: Number(form.guns),
    });

    try {
      const response = await api.post("/api/update_all_config", payload);

      if (response.status >= 200 && response.status < 300) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 2000);
      } else {
        setError("Failed to update settings. Check backend logs.");
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Server error.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="settings-shell">
      <section className="settings-hero">
        <div>
          <p>System settings</p>
          <h1>Station configuration</h1>
        </div>
        <div className="settings-hero-badges">
          <span>
            <SlidersHorizontal size={17} />
            {Number(form.dispensers || 0)} dispensers
          </span>
          <span>
            <RadioTower size={17} />
            {Number(form.guns || 0)} guns
          </span>
        </div>
      </section>

      <form onSubmit={handleSubmit} className="settings-form">
        {isSubmitting && (
          <div className="settings-loading">
            <LoaderCircle size={34} />
          </div>
        )}

        <section className="settings-section">
          <div className="settings-section-heading">
            <span>
              <Settings size={22} />
            </span>
            <div>
              <p>System</p>
              <h2>Hardware layout</h2>
            </div>
          </div>

          <div className="settings-grid two-columns">
            <label className="settings-field">
              <span>Dispensers</span>
              <input
                type="number"
                min="0"
                value={form.dispensers}
                onChange={(e) => handleChange("dispensers", e.target.value)}
              />
            </label>

            <label className="settings-field">
              <span>Guns</span>
              <input
                type="number"
                min="0"
                value={form.guns}
                onChange={(e) => handleChange("guns", e.target.value)}
              />
            </label>
          </div>
        </section>

        <section className="settings-section">
          <div className="settings-section-heading">
            <span>
              <RadioTower size={22} />
            </span>
            <div>
              <p>MQTT</p>
              <h2>Broker</h2>
            </div>
          </div>

          <label className="settings-field">
            <span>MQTT Broker</span>
            <input
              type="text"
              value={form.mqttBroker}
              onChange={(e) => handleChange("mqttBroker", e.target.value)}
              placeholder="mqtt://broker-host"
            />
          </label>
        </section>

        <section className="settings-section">
          <div className="settings-section-heading">
            <span>
              <Database size={22} />
            </span>
            <div>
              <p>Database</p>
              <h2>Connection</h2>
            </div>
          </div>

          <div className="settings-grid">
            <label className="settings-field">
              <span>DB Host</span>
              <input
                type="text"
                value={form.dbHost}
                onChange={(e) => handleChange("dbHost", e.target.value)}
                placeholder="Host"
              />
            </label>

            <label className="settings-field">
              <span>DB User</span>
              <input
                type="text"
                value={form.dbUser}
                onChange={(e) => handleChange("dbUser", e.target.value)}
                placeholder="User"
              />
            </label>

            <label className="settings-field settings-field-wide">
              <span>DB Password</span>
              <input
                type="password"
                value={form.dbPassword}
                onChange={(e) => handleChange("dbPassword", e.target.value)}
                placeholder="Password"
              />
            </label>
          </div>
        </section>

        <section className="settings-section">
          <div className="settings-section-heading">
            <span>
              <SlidersHorizontal size={22} />
            </span>
            <div>
              <p>Logging</p>
              <h2>Interval</h2>
            </div>
          </div>

          <label className="settings-field">
            <span>Log Interval</span>
            <input
              type="number"
              min="1"
              value={form.logInterval}
              onChange={(e) => handleChange("logInterval", e.target.value)}
            />
          </label>
        </section>

        {(success || error) && (
          <div className={`settings-message ${success ? "is-success" : "is-error"}`}>
            {success ? <CheckCircle2 size={18} /> : null}
            {success ? "Settings updated." : error}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting || success}
          className="settings-submit"
        >
          {isSubmitting ? (
            <>
              <LoaderCircle size={18} className="settings-spin" />
              Saving
            </>
          ) : success ? (
            <>
              <CheckCircle2 size={18} />
              Updated
            </>
          ) : (
            <>
              <Save size={18} />
              Save settings
            </>
          )}
        </button>
      </form>
    </main>
  );
};

export default Configuration;
